import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Search, 
  Eye,
  ShoppingCart,
  DollarSign,
  Clock,
  CheckCircle,
  Truck,
  Package,
  XCircle
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OrdersPage = () => {
  const { t } = useLanguage();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`);
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Error al cargar pedidos');
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/orders/${orderId}`, { status: newStatus });
      toast.success(t('savedSuccessfully'));
      fetchOrders();
      if (selectedOrder?.id === orderId) {
        setSelectedOrder({ ...selectedOrder, status: newStatus });
      }
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock, label: t('pending') },
      processing: { color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Package, label: t('processing') },
      shipped: { color: 'bg-purple-100 text-purple-700 border-purple-200', icon: Truck, label: t('shipped') },
      delivered: { color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle, label: t('delivered') },
      cancelled: { color: 'bg-red-100 text-red-700 border-red-200', icon: XCircle, label: t('cancelled') },
    };
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <config.icon size={12} />
        {config.label}
      </Badge>
    );
  };

  const totalRevenue = orders.reduce((sum, order) => sum + order.total, 0);
  
  const filteredOrders = orders.filter(order => {
    const matchesSearch = order.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#467322] border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="orders-page">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold text-[#467322]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          {t('orders')}
        </h1>
        <p className="text-gray-600 mt-1">{t('ordersList')}</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="bg-white border border-[#467322]/20">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-[#467322]/10 rounded-lg">
              <ShoppingCart className="text-[#467322]" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Pedidos</p>
              <p className="text-2xl font-bold text-black">{orders.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border border-[#467322]/20">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <DollarSign className="text-green-600" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-600">{t('totalRevenue')}</p>
              <p className="text-2xl font-bold text-black">${totalRevenue.toFixed(2)}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border border-[#467322]/20">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-amber-100 rounded-lg">
              <Clock className="text-amber-600" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-600">Pendientes</p>
              <p className="text-2xl font-bold text-black">
                {orders.filter(o => o.status === 'pending').length}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border border-[#467322]/20">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <CheckCircle className="text-green-600" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-600">Entregados</p>
              <p className="text-2xl font-bold text-black">
                {orders.filter(o => o.status === 'delivered').length}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-white border border-[#467322]/20">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#467322]" size={20} />
              <Input
                placeholder={`${t('search')}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 border-[#467322]/30 focus:border-[#467322]"
                data-testid="search-orders"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48 border-[#467322]/30">
                <SelectValue placeholder="Filtrar por estado" />
              </SelectTrigger>
              <SelectContent className="bg-white border border-[#467322]/20">
                <SelectItem value="all">{t('all')}</SelectItem>
                <SelectItem value="pending">{t('pending')}</SelectItem>
                <SelectItem value="processing">{t('processing')}</SelectItem>
                <SelectItem value="shipped">{t('shipped')}</SelectItem>
                <SelectItem value="delivered">{t('delivered')}</SelectItem>
                <SelectItem value="cancelled">{t('cancelled')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <Card className="bg-white border border-[#467322]/20">
        <CardHeader>
          <CardTitle className="text-[#467322] flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            <ShoppingCart size={24} />
            {t('ordersList')} ({filteredOrders.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredOrders.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <ShoppingCart size={48} className="mx-auto mb-4 opacity-50" />
              <p>{t('noOrders')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table className="table-striped">
                <TableHeader>
                  <TableRow className="bg-[#467322]">
                    <TableHead className="text-white font-semibold">ID Pedido</TableHead>
                    <TableHead className="text-white font-semibold">{t('client')}</TableHead>
                    <TableHead className="text-white font-semibold">{t('items')}</TableHead>
                    <TableHead className="text-white font-semibold">{t('total')}</TableHead>
                    <TableHead className="text-white font-semibold">{t('status')}</TableHead>
                    <TableHead className="text-white font-semibold">{t('createdAt')}</TableHead>
                    <TableHead className="text-white font-semibold text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredOrders.map((order) => (
                    <TableRow key={order.id} className="hover:bg-[#F2F7F0]">
                      <TableCell className="font-mono text-sm">
                        {order.id.slice(0, 8)}...
                      </TableCell>
                      <TableCell className="font-medium">{order.client_name}</TableCell>
                      <TableCell>{order.items.length} productos</TableCell>
                      <TableCell className="font-semibold text-[#467322]">
                        ${order.total.toFixed(2)}
                      </TableCell>
                      <TableCell>{getStatusBadge(order.status)}</TableCell>
                      <TableCell>
                        {new Date(order.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedOrder(order);
                            setShowDetailsModal(true);
                          }}
                          className="text-[#467322] hover:bg-[#F2F7F0]"
                          data-testid={`view-order-${order.id}`}
                        >
                          <Eye size={16} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Order Details Modal */}
      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="bg-white border border-[#467322]/20 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl text-[#467322]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              {t('orderDetails')}
            </DialogTitle>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-4">
              {/* Order Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-[#F2F7F0] rounded-lg">
                  <p className="text-sm text-gray-500">ID Pedido</p>
                  <p className="font-mono text-sm">{selectedOrder.id.slice(0, 8)}...</p>
                </div>
                <div className="p-3 bg-[#F2F7F0] rounded-lg">
                  <p className="text-sm text-gray-500">{t('client')}</p>
                  <p className="font-semibold">{selectedOrder.client_name}</p>
                </div>
              </div>

              {/* Status Update */}
              <div className="space-y-2">
                <Label className="text-black">{t('orderStatus')}</Label>
                <Select 
                  value={selectedOrder.status} 
                  onValueChange={(value) => updateOrderStatus(selectedOrder.id, value)}
                >
                  <SelectTrigger className="border-[#467322]/30">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border border-[#467322]/20">
                    <SelectItem value="pending">{t('pending')}</SelectItem>
                    <SelectItem value="processing">{t('processing')}</SelectItem>
                    <SelectItem value="shipped">{t('shipped')}</SelectItem>
                    <SelectItem value="delivered">{t('delivered')}</SelectItem>
                    <SelectItem value="cancelled">{t('cancelled')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Items */}
              <div className="space-y-2">
                <Label className="text-black">{t('items')}</Label>
                <div className="border border-[#467322]/20 rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-[#467322]">
                        <TableHead className="text-white">Producto</TableHead>
                        <TableHead className="text-white text-center">{t('quantity')}</TableHead>
                        <TableHead className="text-white text-right">{t('price')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedOrder.items.map((item, idx) => (
                        <TableRow key={item.product_id || idx}>
                          <TableCell>{item.supplement_name}</TableCell>
                          <TableCell className="text-center">{item.quantity}</TableCell>
                          <TableCell className="text-right">${(item.quantity * item.unit_price).toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Total */}
              <div className="flex items-center justify-between p-4 bg-[#467322] rounded-lg">
                <span className="text-white font-semibold">{t('total')}</span>
                <span className="text-2xl font-bold text-white">${selectedOrder.total.toFixed(2)}</span>
              </div>

              {/* Date */}
              <div className="p-3 bg-[#F2F7F0] rounded-lg">
                <p className="text-sm text-gray-500">{t('createdAt')}</p>
                <p className="font-semibold">{new Date(selectedOrder.created_at).toLocaleString()}</p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button
              onClick={() => setShowDetailsModal(false)}
              className="bg-[#467322] hover:bg-[#3A601C] text-white"
            >
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrdersPage;
